import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListChildComponentProps, ListOnScrollProps, FixedSizeList as VirtualList } from 'react-window';
import { Box, LinearProgress, MenuItem, MenuList, Skeleton, Stack, Typography } from '@mui/material';

import ListFilterSelectItem, { ListFilterSelectItemProps } from './ListFilterSelectItem';
import ListFilterItemsCategory, { ListFilterItemsCategoryProps } from './ListFilterItemsCategory';
import { AnalyticsTriggerElementProps } from '@common/types';

export type ListFilterCategoryOrSelectItem = ListFilterSelectItemProps | ListFilterItemsCategoryProps;

export interface ListFilterSelectProps {
  maxHeight?: number;
  items: ListFilterCategoryOrSelectItem[];
  onBottomReached?: () => void;
  bottomOffset?: number;
  isFetching?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  emptyText?: string;
}

function isListFilterSelectItemProps(props: ListFilterCategoryOrSelectItem): props is ListFilterSelectItemProps {
  return props.type === 'checkbox' || props.type === 'radio' || props.type === 'radio-hidden';
}

function isListFilterItemsCategoryProps(props: ListFilterCategoryOrSelectItem): props is ListFilterItemsCategoryProps {
  return props.type === 'category';
}

export default function ListFilterSelect({
  maxHeight = 300,
  items,
  isFetching,
  isLoading,
  disabled,
  emptyText,
  onBottomReached,
  bottomOffset,
  ...analyticsProps
}: ListFilterSelectProps & AnalyticsTriggerElementProps) {
  return (
    <Stack direction="column" justifyContent="center" alignItems="stretch" sx={{ position: 'relative' }}>
      {isLoading && <LinearProgress className="floating" />}
      {isFetching ? (
        <ListFilterSelectIsFetching />
      ) : items.length > 0 ? (
        <ListFilterSelectVirtualized
          items={items}
          maxHeight={maxHeight}
          onBottomReached={onBottomReached}
          bottomOffset={bottomOffset}
          {...analyticsProps}
        />
      ) : (
        <ListFilterSelectEmpty text={emptyText} />
      )}
    </Stack>
  );
}

const ITEM_SIZE = 37;

interface ListFilterSelectVirtualizedProps {
  items: ListFilterCategoryOrSelectItem[];
  maxHeight: number;
  bottomOffset?: number;
  onBottomReached?: () => void;
}

function ListFilterSelectVirtualized({
  items,
  maxHeight,
  bottomOffset = ITEM_SIZE,
  onBottomReached,
  ...analyticsProps
}: ListFilterSelectVirtualizedProps & AnalyticsTriggerElementProps) {
  const [bottomReached, setBottomReached] = useState(false);

  const totalHeight = useMemo(() => items.length * ITEM_SIZE, [items]);
  const height = useMemo(() => Math.min(totalHeight, maxHeight), [maxHeight, totalHeight]);

  useEffect(() => {
    setBottomReached(false);
  }, [totalHeight]);

  const Item = useCallback(
    ({ index, style }: ListChildComponentProps) => {
      const itemProps = items[index];
      if (isListFilterSelectItemProps(itemProps)) {
        return (
          <div style={style} {...analyticsProps}>
            <ListFilterSelectItem {...itemProps} {...analyticsProps} />
          </div>
        );
      }

      if (isListFilterItemsCategoryProps(itemProps)) {
        return (
          <div style={style}>
            <ListFilterItemsCategory {...itemProps} />
          </div>
        );
      }

      return null;
    },
    [items, analyticsProps],
  );

  const handleOnScroll = useCallback(
    (props: ListOnScrollProps) => {
      const toBottom = totalHeight - (height + props.scrollOffset);

      if (totalHeight > height && toBottom < bottomOffset && !bottomReached) {
        setBottomReached(true);
        onBottomReached?.();
      }
    },
    [bottomOffset, bottomReached, height, onBottomReached, totalHeight],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <VirtualList
        height={height}
        width="100%"
        itemSize={ITEM_SIZE}
        itemCount={items.length}
        overscanCount={20}
        onScroll={handleOnScroll}
      >
        {Item}
      </VirtualList>
    </Box>
  );
}

function ListFilterSelectEmpty({ text }: { text?: string }) {
  return (
    <MenuList>
      <MenuItem disabled>
        <Typography variant="caption">
          <em>{text || 'No options'}</em>
        </Typography>
      </MenuItem>
    </MenuList>
  );
}

function ListFilterSelectIsFetching() {
  return (
    <MenuList>
      {Array.from({ length: 3 }, (v, i) => i).map((i) => (
        <MenuItem key={i}>
          <Skeleton variant="text" width="100%" height={20} />
        </MenuItem>
      ))}
    </MenuList>
  );
}
